import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Avatar,
  Box,
  Button,
  InfiniteScroll,
  List,
  Text,
} from '@factorialco/deprecated-design-system'
import { MissingAgreementDataQueryVariables } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { ReturnedData } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/hooks/useMissingAgreementData'

import { useMissingAgreementDataExport } from './export'

interface Props {
  data: ReturnedData
  variables: MissingAgreementDataQueryVariables
  onClose: () => void
}

export const EmployeesWithMissingAgreementsModal = ({ data, variables, onClose }: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const { onExport, isExporting } = useMissingAgreementDataExport(variables)
  const [currentPage, setCurrentPage] = useState(0)

  const { loading, hasMore, setLastCursor, lastCursor, endCursor, data: employeesData } = data

  const totalCount = data?.totalCount ?? 0
  const scrollRef = useRef<HTMLElement>(null)

  if (loading || !data || !totalCount) return null

  const footer = (
    <>
      <Button type="secondary" onClick={onClose} disabled={loading}>
        {i18n.t('common.cancel')}
      </Button>
      <Button type="primary" submit onClick={() => onExport(onClose)} disabled={isExporting}>
        {i18n.t('common.export')}
      </Button>
    </>
  )

  const loadMore = (_: number) => {
    if (lastCursor !== endCursor) {
      setLastCursor(endCursor)

      setCurrentPage((prev) => prev + 1)
    }
  }

  return (
    <Modal
      onClose={onClose}
      footer={footer}
      illustration="money-bag"
      title={i18n.tp('finance.financial_workspace.missing_data.modal.header', {
        count: totalCount,
      })}
      description={i18n.t('finance.financial_workspace.missing_data.modal.subheader')}
    >
      <Box
        ref={scrollRef}
        style={{ flex: '1 1 auto' }}
        component="ul"
        overflowY="auto"
        overflowX="hidden"
        data-scroll-container
        flexGrow
        height="s400"
      >
        <InfiniteScroll
          pageStart={currentPage}
          hasMore={hasMore}
          initialLoad={false}
          useWindow={false}
          loadMore={loadMore}
          getScrollParent={() => scrollRef?.current || null}
          loader={<Loading />}
        >
          <List>
            {employeesData.map((e) => (
              <List.ClickableItem
                key={e.id}
                navigateTo={() =>
                  navigate(navigation.employees.id.contractVersions.fullPath({ id: e.id }))
                }
              >
                <Box alignItems="center" gap="s12" flexDirection="row">
                  <Avatar
                    url={buildUrl(e.employee?.access.avatar)}
                    label={e.employee?.fullName ?? ''}
                    size="s40"
                  />
                  <Text.Normal weight="normal" size="300">
                    {e.employee?.fullName}
                  </Text.Normal>
                </Box>
              </List.ClickableItem>
            ))}
          </List>
        </InfiniteScroll>
      </Box>
    </Modal>
  )
}
